import React, { useMemo, useState, type ReactNode, type PropsWithChildren } from 'react';
import {
  DndContext,
  DragOverlay,
  defaultDropAnimationSideEffects,
  KeyboardSensor,
  PointerSensor,
  type DropAnimation,
  useSensor,
  useSensors,
  type Active,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import type { UniqueIdentifier } from '@dnd-kit/core';
import { DraggableListItem, DragHandle } from './DraggableListItem';

interface BaseItem {
  id: UniqueIdentifier;
  [key: string]: unknown;
}

interface DraggableListProps<T extends BaseItem> {
  items: T[];
  onChange?: (items: T[]) => void;
  renderItem: (item: T) => ReactNode;
}

const dropAnimationConfig: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '0.4',
      },
    },
  }),
};

function SortableDragOverlay({ children }: PropsWithChildren<{}>) {
  return <DragOverlay dropAnimation={dropAnimationConfig}>{children}</DragOverlay>;
}

export function DraggableList<T extends BaseItem>({
  items,
  onChange,
  renderItem,
}: DraggableListProps<T>) {
  const [active, setActive] = useState<Active | null>(null);
  const activeItem = useMemo(() => items.find((item) => item.id === active?.id), [active, items]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleChange = (newItems: T[]) => {
    if (onChange) onChange(newItems);
  };

  return (
    <DndContext
      sensors={sensors}
      onDragStart={({ active }) => setActive(active)}
      onDragEnd={({ active, over }) => {
        if (over && active.id !== over.id) {
          const oldIndex = items.findIndex((i) => i.id === active.id);
          const newIndex = items.findIndex((i) => i.id === over.id);
          handleChange(arrayMove(items, oldIndex, newIndex));
        }
        setActive(null);
      }}
      onDragCancel={() => setActive(null)}
    >
      <SortableContext items={items}>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {items.map((item) => (
            <React.Fragment key={item.id}>{renderItem(item)}</React.Fragment>
          ))}
        </ul>
      </SortableContext>
      <SortableDragOverlay>{activeItem ? renderItem(activeItem) : null}</SortableDragOverlay>
    </DndContext>
  );
}

DraggableList.Item = DraggableListItem;
DraggableList.DragHandle = DragHandle;
